import { useState } from 'react'

import { Menubar, MenubarContent, MenubarItem, MenubarMenu, MenubarTrigger } from '@/components/ui/menubar'
import { PATH_NAME } from '@/config/path'
import { HistoryButton } from '@/containers/histories/HistoryButton'
import { WalletConnection } from '@/containers/wallet-connection'
import { useIsMd } from '@/hooks'
import {
  CommonArrowDropDown,
  CommonChangeCircle,
  CommonLogo,
  CommonLogoIcon,
  CommonOrbit,
  CommonRefresh,
  CommonRepeat,
  CommonThreeDot,
} from '@/icons/icons'
import { cn } from '@/lib'

import { useTranslation } from 'react-i18next'
import { Link, useLocation, useNavigate } from 'react-router-dom'

import { Menu } from './Menu'

import RenderIf from '../ui/render-if'
import { Text } from '../ui/text'

interface MenuItemProps {
  label: string
  icon?: React.ReactNode
  href?: string
  isShowBorder?: boolean
  isShowLabel?: boolean
  isActive?: boolean
}

interface MenuDropdownItem {
  label: string
  icon?: React.ReactNode
  onClick?: () => void
  href?: string
  active?: (pathname: string) => boolean
}
enum MenuIds {
  SWAP = 'swap',
  FIXED_SWAP_DEPOSIT = 'fixed-swap-deposit',
  REFERRAL_HUB = 'referral-hub',
  MORE = 'more',
}

const SWAP_PATH = [PATH_NAME.HOME, PATH_NAME.SWAP]
const FIXED_SWAP_PATH = [
  PATH_NAME.FIXED_SWAP,
  PATH_NAME.FIXED_SWAP_DEPOSIT,
  PATH_NAME.FIXED_SWAP_DEPOSIT_DETAIL,
  PATH_NAME.FIXED_SWAP_DEPOSIT_HISTORY,
  PATH_NAME.FIXED_SWAP_VAULT,
]

const MENU: MenuConfigItem[] = [
  {
    id: MenuIds.SWAP,
    label: 'menu.SWAP',
    href: PATH_NAME.HOME,
    icon: <CommonRepeat className='size-6 md:size-[1.125rem] ' />,
    active: (pathname: string) => SWAP_PATH.includes(pathname) || FIXED_SWAP_PATH.includes(pathname),
    dropdownItems: [
      {
        label: 'title.TURBO_SWAP',
        icon: <CommonRefresh className='size-6' />,
        href: PATH_NAME.HOME,
        active: (pathname: string) => SWAP_PATH.includes(pathname),
      },
      {
        label: 'title.FIXED_SWAP',
        icon: <CommonChangeCircle className='size-6' />,
        href: PATH_NAME.FIXED_SWAP_VAULT,
        active: (pathname: string) => FIXED_SWAP_PATH.includes(pathname),
      },
    ],
  },
  // {
  //   label: 'Loan',
  //   icon: <CommonCurrencyExchange className='size-[1.125rem]' />,
  // },
  {
    id: MenuIds.REFERRAL_HUB,
    label: 'title.REFERRAL_HUB',
    icon: <CommonOrbit className='size-6 md:size-[1.125rem]' />,
    href: PATH_NAME.REFERRAL_HUB,
    active: (pathname: string) =>
      [PATH_NAME.REFERRAL_HUB, PATH_NAME.CLAIM_HISTORY].includes(pathname) ||
      pathname.includes(PATH_NAME.CLAIM_HISTORY) ||
      pathname.includes(PATH_NAME.REFERRAL_HUB),
  },
]
interface MenuConfigItem {
  id: MenuIds
  label: string
  icon?: React.ReactNode
  href?: string
  dropdownItems?: MenuDropdownItem[]
  active?: (pathname: string) => boolean
}

const Icon = ({
  icon,
  isShowBorder,
  isActive,
}: {
  icon: React.ReactNode
  isShowBorder?: boolean
  isActive?: boolean
}) => {
  return (
    <div
      className={cn(
        'flex items-center transition-all duration-300 md:hover:text-main-100 md:group-hover:text-main-100 group-data-[state=open]:text-main-100',
        {
          'rounded-sm ring-[1px]  md:text-white-48 [@media(max-resolution:1.9dppx)]:ring-[1px] ring-white-10 md:group-hover:ring-main-100 md:group-hover:ring-[1px] group-data-[state=open]:ring-main-100  group-data-[state=open]:ring-[1px]  md:group-data-[state=open]:ring-[1px] md:ring-[0.5px]  md:group-hover:bg-main-8 group-data-[state=open]:bg-main-8 p-1 md:p-[0.1875rem]':
            isShowBorder,
          '!text-main-100': isActive,
          'ring-main-100 ring-[1px] md:ring-[0.5px]': isActive && isShowBorder,
        }
      )}
    >
      {icon}
    </div>
  )
}

export const MenuTriggerContent = ({
  label,
  icon,
  isShowBorder = true,
  isShowLabel = true,
  isActive = false,
}: MenuItemProps) => {
  const { t } = useTranslation()
  return (
    <div
      className={cn(
        'flex items-center gap-2 text-size-16 md:group-hover:text-main-100 group-data-[state=open]:text-main-100 transition-all duration-300',
        isActive && 'text-main-100'
      )}
    >
      <RenderIf condition={!!icon}>
        <Icon icon={icon} isShowBorder={isShowBorder} isActive={isActive} />
      </RenderIf>
      <RenderIf condition={isShowLabel}>{t(label)}</RenderIf>
    </div>
  )
}

function renderMenu(
  menu: MenuConfigItem,
  isMd: boolean,
  pathname: string,
  setOpenMenu: (value: string, href?: string) => void
) {
  const isMenuActive = menu.active ? menu.active(pathname) : false
  if (menu.dropdownItems) {
    return (
      <MenubarMenu key={menu.id} value={menu.id}>
        <MenubarTrigger
          onMouseEnter={() => setOpenMenu(menu.id)}
          onClick={() => setOpenMenu(menu.id, menu.href)}
          className='flex items-center group'
        >
          <MenuTriggerContent label={menu.label} icon={menu.icon} isShowLabel={isMd} isActive={isMenuActive} />
          {isMd && (
            <CommonArrowDropDown
              className={cn(
                'size-6 transition-transform duration-300 group-data-[state=open]:rotate-180 group-data-[state=open]:text-main-100',
                { 'text-main-100': isMenuActive }
              )}
              aria-hidden='true'
            />
          )}
        </MenubarTrigger>
        <MenubarContent
          onInteractOutside={() => setOpenMenu('')}
          align='center'
          className='p-0 min-w-[180px] md:min-w-[200px]'
        >
          {menu.dropdownItems.map((item) => {
            const isItemActive = item.active ? item.active(pathname) : false
            return (
              <MenubarItem key={item.label} onClick={() => setOpenMenu('')}>
                <Link
                  to={item.href!}
                  className={cn('group flex items-center gap-2', { 'text-main-100': isItemActive })}
                >
                  <MenuTriggerContent
                    label={item.label}
                    icon={item.icon}
                    isShowBorder={false}
                    isActive={isItemActive}
                  />
                </Link>
              </MenubarItem>
            )
          })}
        </MenubarContent>
      </MenubarMenu>
    )
  } else {
    return (
      <MenubarMenu key={menu.id} value={menu.id}>
        <MenubarTrigger
          className='flex items-center group'
          onMouseEnter={() => setOpenMenu(menu.id)}
          onMouseLeave={() => setOpenMenu('')}
        >
          <Link to={menu.href!} className={cn('flex items-center gap-2', { 'text-main-100': isMenuActive })}>
            <MenuTriggerContent label={menu.label} icon={menu.icon} isShowLabel={isMd} isActive={isMenuActive} />
          </Link>
        </MenubarTrigger>
      </MenubarMenu>
    )
  }
}

export function HeaderV2() {
  const navigate = useNavigate()
  const isMd = useIsMd()
  const { pathname } = useLocation()
  const [openMenu, setOpenMenu] = useState('')
  const handleSetOpenMenu = (value: string, href?: string) => {
    setOpenMenu(value)
    if (href && isMd) {
      navigate(href)
      setOpenMenu('')
    }
  }
  const { t } = useTranslation()
  return (
    <header className='ring-[1px] md:ring-[0.5px] [@media(max-resolution:1.9dppx)]:ring-[1px] bg-black-12 ring-white-12 dark:ring-black-12 p-4 md:px-12 py-4 flex justify-between'>
      <div className='flex gap-4  md:gap-10 items-center'>
        <div onClick={() => navigate(PATH_NAME.HOME)} className='flex items-center cursor-pointer gap-2'>
          {isMd ? (
            <CommonLogo className='w-[138px] h-[32px] my-auto fill-white-100' />
          ) : (
            <CommonLogoIcon className='w-[28px] h-[18px] fill-white-100' />
          )}
          <Text color='white-48' className='px-1 rounded-[2px] border border-white-12' size={12}>
            BETA
          </Text>
        </div>
        <Menubar className='flex gap-[8px] md:gap-4' value={openMenu}>
          {MENU.map((menu) => renderMenu(menu, isMd, pathname, handleSetOpenMenu))}
          <MenubarMenu value={MenuIds.MORE}>
            <MenubarTrigger
              className='group'
              onMouseEnter={() => setOpenMenu(MenuIds.MORE)}
              onClick={() => setOpenMenu(MenuIds.MORE)}
            >
              <MenuTriggerContent
                label={t('title.MORE')}
                icon={<CommonThreeDot className='size-6 md:size-[1.125rem]' />}
                isShowLabel={isMd}
                isActive={false}
              />
              {isMd && (
                <CommonArrowDropDown
                  className='size-6 transition-transform duration-300 group-data-[state=open]:rotate-180 group-data-[state=open]:text-main-100'
                  aria-hidden='true'
                />
              )}
            </MenubarTrigger>
            <MenubarContent
              onInteractOutside={() => setOpenMenu('')}
              align='center'
              className='px-6 py-4 min-w-[180px] md:min-w-[200px]'
            >
              <Menu setOpenMenu={setOpenMenu} />
            </MenubarContent>
          </MenubarMenu>
        </Menubar>
      </div>
      <div className='flex items-center gap-2'>
        <WalletConnection />
        <HistoryButton />
      </div>
    </header>
  )
}
